import {
  PERSONDATA_TOPICS,
  PersondataTopicId,
  ReportTextTemplateKey,
  TemplateVariant,
} from '../../../report-templates/reportTextTemplateKeys';
import { interpolatePlaceholders } from './interpolatePlaceholders';

export type OverwriteMode = 'overwrite-all' | 'only-empty';

export type SkipReason = 'weak-link' | 'empty-template' | 'not-empty';

export type ReportTextFieldId =
  | 'workRegisterResult'
  | 'workRegisterActions'
  | 'identificationResult'
  | 'identificationActions'
  | 'id06DeviationsResult'
  | 'id06DeviationsActions'
  | 'safetyEquipmentResult'
  | 'safetyEquipmentActions'
  | 'workPermitResult'
  | 'workPermitActions'
  | 'secondmentRegulationsResult'
  | 'secondmentRegulationsActions'
  | 'shellProtectionResult'
  | 'shellProtectionActions'
  | 'workEnvironmentResult'
  | 'workEnvironmentActions'
  | 'preApprovedSubcontractorResult'
  | 'preApprovedSubcontractorActions'
  | 'intoxicationResult'
  | 'intoxicationActions'
  | 'otherResult'
  | 'otherActions';

export type PlanTopicField = {
  fieldId: ReportTextFieldId;
  label: string;
  kind: 'Result' | 'Actions';
};

export type PlanTopic = {
  id: string;
  title: string;
  /** Persondata topic id when mappable; undefined for weak-link-only topics. */
  persondataTopicId?: PersondataTopicId;
  fields: PlanTopicField[];
};

export type WritePlanEntry = {
  fieldId: ReportTextFieldId;
  label: string;
  html: string;
  variant: TemplateVariant;
};

export type SkipPlanEntry = {
  fieldId: ReportTextFieldId;
  label: string;
  reason: SkipReason;
  reasonLabel: string;
};

export type PlanReportTextDraftInput = {
  templates: Partial<Record<ReportTextTemplateKey, string>>;
  placeholders: Record<string, string>;
  currentValues: Partial<Record<ReportTextFieldId, string | undefined>>;
  overwriteMode: OverwriteMode;
  /** Topics that may generate; all others are weak-link skips. */
  activeTopicIds: readonly PersondataTopicId[];
};

export type PlanReportTextDraftResult = {
  writes: WritePlanEntry[];
  skips: SkipPlanEntry[];
};

const SKIP_REASON_LABELS: Record<SkipReason, string> = {
  'weak-link': 'Ingen Persondata-koppling',
  'empty-template': 'Tom mallvariant',
  'not-empty': 'Fältet har redan innehåll',
};

/** Weak-link topics that appear on Resultat/åtgärder but have no v1 Persondata mapping. */
const NON_PERSONDATA_TOPICS: PlanTopic[] = [
  {
    id: 'shellProtection',
    title: 'Skalskydd',
    fields: [
      {
        fieldId: 'shellProtectionResult',
        label: 'Skalskydd – Resultat',
        kind: 'Result',
      },
      {
        fieldId: 'shellProtectionActions',
        label: 'Skalskydd – Åtgärder',
        kind: 'Actions',
      },
    ],
  },
  {
    id: 'workEnvironment',
    title: 'Arbetsmiljö',
    fields: [
      {
        fieldId: 'workEnvironmentResult',
        label: 'Arbetsmiljö – Resultat',
        kind: 'Result',
      },
      {
        fieldId: 'workEnvironmentActions',
        label: 'Arbetsmiljö – Åtgärder',
        kind: 'Actions',
      },
    ],
  },
  {
    id: 'preApprovedSubcontractor',
    title: 'Förgodkända UE',
    fields: [
      {
        fieldId: 'preApprovedSubcontractorResult',
        label: 'Förgodkända UE – Resultat',
        kind: 'Result',
      },
      {
        fieldId: 'preApprovedSubcontractorActions',
        label: 'Förgodkända UE – Åtgärder',
        kind: 'Actions',
      },
    ],
  },
  {
    id: 'intoxication',
    title: 'Onykterhet',
    fields: [
      {
        fieldId: 'intoxicationResult',
        label: 'Onykterhet – Resultat',
        kind: 'Result',
      },
      {
        fieldId: 'intoxicationActions',
        label: 'Onykterhet – Åtgärder',
        kind: 'Actions',
      },
    ],
  },
  {
    id: 'other',
    title: 'Övrig kontrollverksamhet',
    fields: [
      {
        fieldId: 'otherResult',
        label: 'Övrig kontrollverksamhet – Resultat',
        kind: 'Result',
      },
      {
        fieldId: 'otherActions',
        label: 'Övrig kontrollverksamhet – Åtgärder',
        kind: 'Actions',
      },
    ],
  },
];

const PERSONDATA_PLAN_TOPICS: PlanTopic[] = PERSONDATA_TOPICS.map((topic) => ({
  id: topic.id,
  title: topic.title,
  persondataTopicId: topic.id,
  fields: [
    {
      fieldId: `${topic.id}Result` as ReportTextFieldId,
      label: `${topic.title} – Resultat`,
      kind: 'Result',
    },
    {
      fieldId: `${topic.id}Actions` as ReportTextFieldId,
      label: `${topic.title} – Åtgärder`,
      kind: 'Actions',
    },
  ],
}));

export const ALL_REPORT_TEXT_PLAN_TOPICS: PlanTopic[] = [
  ...PERSONDATA_PLAN_TOPICS,
  ...NON_PERSONDATA_TOPICS,
];

/**
 * Full v1 Persondata topic allowlist (issues/004).
 * Topics outside this set remain weak-link skips.
 */
export const ACTIVE_PERSONDATA_TOPIC_IDS_V1: readonly PersondataTopicId[] = [
  'workRegister',
  'identification',
  'id06Deviations',
  'safetyEquipment',
  'workPermit',
  'secondmentRegulations',
] as const;

const toNumber = (value: string | undefined): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const isBlankHtml = (value: string | undefined): boolean => {
  if (!value) return true;
  return (
    value.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length === 0
  );
};

/**
 * Selects WhenDeviations vs WhenClear from control-level count rules.
 * EPL uses OR across not-controlled and additional personnel counts.
 */
export function selectTemplateVariant(
  topicId: PersondataTopicId,
  placeholders: Record<string, string>
): TemplateVariant {
  switch (topicId) {
    case 'workRegister': {
      const notControlled = toNumber(
        placeholders.numberOfNotControlledPersonnel
      );
      const additional = toNumber(placeholders.numberOfAdditionalPersonnel);
      return notControlled > 0 || additional > 0
        ? 'WhenDeviations'
        : 'WhenClear';
    }
    case 'identification':
      return toNumber(placeholders.numberOfPersonnelWithInvalidIdentification) >
        0
        ? 'WhenDeviations'
        : 'WhenClear';
    case 'id06Deviations':
      return toNumber(placeholders.numberOfPersonnelWithIdCardDeviations) > 0
        ? 'WhenDeviations'
        : 'WhenClear';
    case 'safetyEquipment':
      return toNumber(placeholders.numberOfPersonnelWithoutSafetyEquipment) > 0
        ? 'WhenDeviations'
        : 'WhenClear';
    case 'workPermit':
      return toNumber(placeholders.numberOfPersonnelWithoutWorkPermit) > 0
        ? 'WhenDeviations'
        : 'WhenClear';
    case 'secondmentRegulations':
      return toNumber(placeholders.numberOfPersonnelWithoutCertificate) > 0
        ? 'WhenDeviations'
        : 'WhenClear';
    default:
      return 'WhenClear';
  }
}

const templateKeyFor = (
  topicId: PersondataTopicId,
  kind: 'Result' | 'Actions',
  variant: TemplateVariant
): ReportTextTemplateKey => {
  const topic = PERSONDATA_TOPICS.find((t) => t.id === topicId);
  if (!topic) {
    throw new Error(`Unknown persondata topic: ${topicId}`);
  }
  return kind === 'Result' ? topic.result[variant] : topic.actions[variant];
};

const pushSkip = (
  skips: SkipPlanEntry[],
  field: PlanTopicField,
  reason: SkipReason
) => {
  skips.push({
    fieldId: field.fieldId,
    label: field.label,
    reason,
    reasonLabel: SKIP_REASON_LABELS[reason],
  });
};

/**
 * Pure draft planner: allowlist, variant selection, empty-template skips,
 * and overwrite-all vs only-empty filtering. Never touches ActionsNeeded.
 */
export function planReportTextDraft(
  input: PlanReportTextDraftInput
): PlanReportTextDraftResult {
  const writes: WritePlanEntry[] = [];
  const skips: SkipPlanEntry[] = [];
  const active = new Set(input.activeTopicIds);

  for (const topic of ALL_REPORT_TEXT_PLAN_TOPICS) {
    if (
      topic.persondataTopicId === undefined ||
      !active.has(topic.persondataTopicId)
    ) {
      for (const field of topic.fields) {
        pushSkip(skips, field, 'weak-link');
      }
      continue;
    }

    const topicId = topic.persondataTopicId;
    const variant = selectTemplateVariant(topicId, input.placeholders);

    for (const field of topic.fields) {
      if (
        input.overwriteMode === 'only-empty' &&
        !isBlankHtml(input.currentValues[field.fieldId])
      ) {
        pushSkip(skips, field, 'not-empty');
        continue;
      }

      const key = templateKeyFor(topicId, field.kind, variant);
      const body = input.templates[key];

      if (body === undefined || isBlankHtml(body)) {
        pushSkip(skips, field, 'empty-template');
        continue;
      }

      writes.push({
        fieldId: field.fieldId,
        label: field.label,
        html: interpolatePlaceholders(body, input.placeholders),
        variant,
      });
    }
  }

  return { writes, skips };
}
